import {
  isExplicitListenerUserType,
  isListenerUserType,
  listenerSignupUserType,
  talkerSignupUserType,
} from './listenerUserType';
import { LISTENER_ONBOARDING_STARTED_KEY, isPendingBecomeListener } from './listenerOnboarding';
import {
  editListeningProfileRoute,
  hasOwnListeningProfile,
  hasPublishedListeningProfile,
  pickOwnListeningProfile,
} from './ownListeningProfile';

export const ACCOUNT_ROLE_TALK = 'talk';
export const ACCOUNT_ROLE_LISTEN = 'listen';
export const ACCOUNT_ROLE_BOTH = 'both';
export const ACCOUNT_ROLES = [ACCOUNT_ROLE_TALK, ACCOUNT_ROLE_LISTEN, ACCOUNT_ROLE_BOTH];

export const ACCOUNT_ROLE_KEY = 'accountRole';
export const NEEDS_ACCOUNT_ROLE_KEY = 'needsAccountRole';

export const ACCOUNT_SETUP_TALKER = 'talker';
export const ACCOUNT_SETUP_LISTENER = 'listener';
export const ACCOUNT_SETUP_BOTH = 'both';
export const ACCOUNT_ROLE_EDIT_PARAM = 'edit';
export const ACCOUNT_ROLE_EDIT_VALUE = '1';
export const accountRoleEditTo = {
  search: `?${ACCOUNT_ROLE_EDIT_PARAM}=${ACCOUNT_ROLE_EDIT_VALUE}`,
};

/**
 * True when /choose-role was opened from Account settings to change setup.
 *
 * @param {string} [search]
 * @returns {boolean}
 */
export const isAccountRoleEditSearch = search => {
  if (!search || typeof search !== 'string') {
    return false;
  }
  const raw = search.charAt(0) === '?' ? search.slice(1) : search;
  return new URLSearchParams(raw).get(ACCOUNT_ROLE_EDIT_PARAM) === ACCOUNT_ROLE_EDIT_VALUE;
};

const profileOf = currentUser => currentUser?.attributes?.profile || {};
const publicDataOf = currentUser => profileOf(currentUser).publicData || {};
const privateDataOf = currentUser => profileOf(currentUser).privateData || {};
const protectedDataOf = currentUser => profileOf(currentUser).protectedData || {};

const roleFrom = data => {
  const role = data?.[ACCOUNT_ROLE_KEY];
  return ACCOUNT_ROLES.includes(role) ? role : null;
};

/**
 * Listen signup stores userType even when Console drops custom role keys.
 * Never treat that as Talk-only.
 *
 * @param {Object} [currentUser]
 * @returns {'listen'|null}
 */
export const inferredAccountRoleFromUserType = currentUser => {
  const userType =
    publicDataOf(currentUser).userType ||
    privateDataOf(currentUser).userType ||
    protectedDataOf(currentUser).userType;
  return isExplicitListenerUserType(userType) ? ACCOUNT_ROLE_LISTEN : null;
};

/**
 * Durable Talk / Listen / Both choice. Public first, then private / protected,
 * then listener userType so Listen intent cannot silently become Talk.
 *
 * @param {Object} [currentUser]
 * @returns {'talk'|'listen'|'both'|null}
 */
export const getAccountRole = currentUser => {
  return (
    roleFrom(publicDataOf(currentUser)) ||
    roleFrom(privateDataOf(currentUser)) ||
    roleFrom(protectedDataOf(currentUser)) ||
    inferredAccountRoleFromUserType(currentUser)
  );
};

/**
 * Pending-chooser flag. Written to public and private so a dropped public key
 * cannot skip the Talk / Listen / Both step.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
const hasPendingAccountRoleFlag = currentUser =>
  publicDataOf(currentUser)[NEEDS_ACCOUNT_ROLE_KEY] === true ||
  privateDataOf(currentUser)[NEEDS_ACCOUNT_ROLE_KEY] === true;

/**
 * True only for brand-new accounts that still owe the Talk / Listen / Both step.
 * Existing users without this flag are not trapped on the chooser.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {boolean}
 */
export const needsAccountRoleChoice = (currentUser, { currentUserHasListings = false } = {}) => {
  if (!currentUser?.id) {
    return false;
  }
  if (currentUserHasListings) {
    return false;
  }
  if (getAccountRole(currentUser)) {
    return false;
  }
  return hasPendingAccountRoleFlag(currentUser);
};

/**
 * Console user type that matches the chosen role.
 *
 * @param {'talk'|'listen'|'both'} role
 * @param {Array} [userTypes]
 * @returns {string|null}
 */
export const userTypeForAccountRole = (role, userTypes = []) => {
  if (role === ACCOUNT_ROLE_TALK) {
    return talkerSignupUserType(userTypes);
  }
  return listenerSignupUserType(userTypes) || talkerSignupUserType(userTypes);
};

/**
 * Where to send someone right after they pick Talk / Listen / Both.
 * Always Dashboard. Listen / Both finish an incomplete Listening profile
 * from a banner on the Listening tab — never auto-open Details.
 *
 * @param {'talk'|'listen'|'both'|null} role
 * @returns {{ name: string }}
 */
export const destinationAfterAccountRole = role => {
  // Login / role-choice always land on Dashboard. Incomplete Listen / Both
  // finish the Listening profile from a Dashboard banner — never Details.
  if (role === ACCOUNT_ROLE_LISTEN || role === ACCOUNT_ROLE_BOTH) {
    return { name: 'DashboardPage', search: '?tab=listening' };
  }
  return { name: 'DashboardPage', search: '?tab=talking' };
};

/**
 * After a Listening profile is published (or the wizard finishes), go to
 * Dashboard — never Search. Talk publish is uncommon; still Dashboard.
 *
 * @param {'talk'|'listen'|'both'|null} [_role]
 * @returns {{ name: string }}
 */
export const destinationAfterListeningProfileSetup = _role => ({ name: 'DashboardPage' });

/**
 * Dashboard and Profile tabs for this account.
 *
 * Saved role is the only chrome source: Talk → Talking, Listen → Listening,
 * Both → Listening | Talking. A leftover listing never upgrades Talk-only
 * to Both. Become a listener writes a started flag only — Both waits until
 * a Listening profile is published. An unfinished Both write (LIVE recover)
 * stays Talk chrome. No saved role: a draft/profile is Listen work.
 *
 * @param {Object} [params]
 * @returns {{ showListening: boolean, showTalking: boolean }}
 */
export const dashboardLayoutForUser = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
      currentUserHasListingsFetched,
    })
  ) {
    return { showListening: false, showTalking: true };
  }
  const role = getAccountRole(currentUser);
  if (role === ACCOUNT_ROLE_LISTEN) {
    return { showListening: true, showTalking: false };
  }
  if (role === ACCOUNT_ROLE_TALK) {
    return { showListening: false, showTalking: true };
  }
  if (role === ACCOUNT_ROLE_BOTH) {
    return { showListening: true, showTalking: true };
  }
  const hasProfile = hasOwnListeningProfile({
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  // No saved role: only an explicit Listen userType is Listen work.
  // A leftover draft or Sharetribe `provider` type must stay Talking —
  // talker-only accounts never inherit listener chrome from a listing.
  if (hasProfile && inferredAccountRoleFromUserType(currentUser) === ACCOUNT_ROLE_LISTEN) {
    return { showListening: true, showTalking: false };
  }
  return { showListening: false, showTalking: true };
};

/**
 * Last-used hamburger mode. Same keys as siteNavMode (protected then private).
 *
 * @param {Object} [currentUser]
 * @returns {'talker'|'listener'|null}
 */
const preferredNavModeOf = currentUser => {
  const raw = protectedDataOf(currentUser).preferred || privateDataOf(currentUser).preferred;
  if (raw === 'talker' || raw === 'talk') {
    return 'talker';
  }
  if (raw === 'listener' || raw === 'listen') {
    return 'listener';
  }
  return null;
};

/**
 * Signed-in menu Dashboard item. Talk-only opens Talking, Listen-only opens
 * Listening. Dual-role opens the last-used mode (Listener by default).
 *
 * @param {Object} [params]
 * @returns {{ kind: 'listening'|'talking', search: string }}
 */
export const dashboardNavForUser = (params = {}) => {
  const { showListening, showTalking } = dashboardLayoutForUser(params);
  if (showListening && showTalking) {
    if (preferredNavModeOf(params.currentUser) === 'talker') {
      return { kind: 'talking', search: '?tab=talking' };
    }
    return { kind: 'listening', search: '?tab=listening' };
  }
  if (showListening) {
    return { kind: 'listening', search: '?tab=listening' };
  }
  return { kind: 'talking', search: '?tab=talking' };
};

/**
 * Translation id for the signed-in Dashboard menu item.
 * Always “Dashboard”. Listening | Talking tabs stay on the page.
 *
 * @param {Object} [_params]
 * @param {'TopbarDesktop'|'TopbarMobileMenu'} [prefix]
 * @returns {string}
 */
export const dashboardNavLabelId = (_params = {}, prefix = 'TopbarDesktop') =>
  `${prefix}.dashboard`;

/**
 * Inbox sides this account can open.
 * Talk-only: Talking. Listen-only: Listening. Both: Listening | Talking.
 * Legacy users without a saved role follow the Dashboard layout.
 *
 * @param {Object} [params]
 * @returns {{ showListening: boolean, showTalking: boolean }}
 */
export const inboxLayoutForUser = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserHasListingsFetched,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return { showListening: false, showTalking: true };
  }
  const role = getAccountRole(currentUser);
  if (role === ACCOUNT_ROLE_TALK) {
    return { showListening: false, showTalking: true };
  }
  if (role === ACCOUNT_ROLE_LISTEN) {
    return { showListening: true, showTalking: false };
  }
  if (role === ACCOUNT_ROLE_BOTH) {
    return { showListening: true, showTalking: true };
  }
  return dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
};

/**
 * Default Inbox path tab. Listening first for Both and Listen-only.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @returns {'sales'|'orders'}
 */
export const defaultInboxTabForUser = (currentUser, params = {}) => {
  const { showListening, showTalking } = inboxLayoutForUser({ currentUser, ...params });
  if (showTalking && !showListening) {
    return 'orders';
  }
  return 'sales';
};

/**
 * Keep Talk-only on Talking and Listen-only on Listening.
 *
 * @param {string} tab
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @returns {'sales'|'orders'}
 */
export const resolveInboxTab = (tab, currentUser, params = {}) => {
  const { showListening, showTalking } = inboxLayoutForUser({ currentUser, ...params });
  if (tab === 'sales' && showListening) {
    return 'sales';
  }
  if (tab === 'orders' && showTalking) {
    return 'orders';
  }
  return defaultInboxTabForUser(currentUser, params);
};

/**
 * Listen / Both must finish a Listening profile before they browse Search.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {boolean}
 */
export const needsListeningProfileSetup = (
  currentUser,
  { currentUserHasListings = false, currentUserOwnListing = null, ownListings = [] } = {}
) => {
  if (!currentUser?.id) {
    return false;
  }
  if (
    hasOwnListeningProfile({
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = getAccountRole(currentUser);
  return role === ACCOUNT_ROLE_LISTEN || role === ACCOUNT_ROLE_BOTH;
};

/**
 * Listen-only without a profile should not stay on Search. Send them to
 * Dashboard (Listening tab + finish-setup banner) — never /l/new or Details.
 * Both keeps Talk Search.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @returns {boolean}
 */
export const shouldRedirectToListeningProfileSetup = (
  currentUser,
  {
    currentUserHasListings = false,
    currentUserOwnListing = null,
    ownListings = [],
    currentUserHasListingsFetched = false,
  } = {}
) => {
  if (
    !needsListeningProfileSetup(currentUser, {
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  if (getAccountRole(currentUser) === ACCOUNT_ROLE_BOTH) {
    return false;
  }
  return currentUserHasListingsFetched !== false;
};

/**
 * Listening tab notice: Listen / Both still owe a published profile.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const showIncompleteListeningProfileNotice = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (getAccountRole(currentUser) === ACCOUNT_ROLE_TALK) {
    return false;
  }
  const { showListening } = dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserHasListingsFetched,
    currentUserOwnListing,
    ownListings,
  });
  if (!showListening) {
    return false;
  }
  return !hasPublishedListeningProfile({
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
};

/**
 * After login or email verify: chooser if owed, otherwise Dashboard.
 * Never auto-open Listening profile Details.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {{ name: string }}
 */
export const destinationAfterVerifiedAccount = (
  currentUser,
  { currentUserHasListings = false, currentUserOwnListing = null, ownListings = [] } = {}
) => {
  if (needsAccountRoleChoice(currentUser, { currentUserHasListings })) {
    return { name: 'ChooseAccountRolePage' };
  }
  // Fresh signup that finished email + ID: show the welcome picker once.
  try {
    if (typeof window !== 'undefined' && window.sessionStorage?.getItem('slWelcomePending')) {
      return { name: 'WelcomePage' };
    }
  } catch (e) {
    // ignore storage errors
  }
  const { showListening, showTalking } = dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  if (showListening && !showTalking) {
    return { name: 'DashboardPage', search: '?tab=listening' };
  }
  if (showListening && showTalking) {
    return { name: 'DashboardPage', search: '?tab=listening' };
  }
  return { name: 'DashboardPage', search: '?tab=talking' };
};

export const IDENTITY_STANDIN_DONE_PARAM = 'standin';
export const IDENTITY_STANDIN_DONE_VALUE = 'done';

/**
 * Talk-only accounts should leave Your profile after the TEST stand-in.
 * Listen / Both stay so they can finish a Listening profile.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @returns {boolean}
 */
export const shouldAutoAdvanceAfterIdentityStandin = (
  currentUser,
  { currentUserHasListings = false, currentUserOwnListing = null, ownListings = [] } = {}
) => {
  const { showListening, showTalking } = dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  return !showListening && !!showTalking;
};

/**
 * Dashboard destination after a successful TEST stand-in, with a toast query.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @returns {{ name: string, search?: string }}
 */
export const destinationAfterIdentityStandin = (currentUser, params = {}) => {
  const dest = destinationAfterVerifiedAccount(currentUser, params);
  if (dest.name !== 'DashboardPage') {
    return dest;
  }
  const search = dest.search
    ? `${dest.search}&${IDENTITY_STANDIN_DONE_PARAM}=${IDENTITY_STANDIN_DONE_VALUE}`
    : `?${IDENTITY_STANDIN_DONE_PARAM}=${IDENTITY_STANDIN_DONE_VALUE}`;
  return { ...dest, search };
};

/**
 * True when Dashboard should show the stand-in success toast.
 *
 * @param {string} [search]
 * @returns {boolean}
 */
export const hasJustCompletedIdentityStandin = search => {
  if (!search || typeof search !== 'string') {
    return false;
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  return query.split('&').some(part => {
    const [key, value] = part.split('=');
    return key === IDENTITY_STANDIN_DONE_PARAM && value === IDENTITY_STANDIN_DONE_VALUE;
  });
};

/**
 * Drop standin=done so a refresh or dismiss does not keep nagging.
 *
 * @param {string} [search]
 * @returns {string}
 */
export const withoutIdentityStandinDoneParam = search => {
  if (!search || typeof search !== 'string') {
    return '';
  }
  const query = search.startsWith('?') ? search.slice(1) : search;
  const kept = query.split('&').filter(part => {
    if (!part) {
      return false;
    }
    const [key] = part.split('=');
    return key !== IDENTITY_STANDIN_DONE_PARAM;
  });
  return kept.length ? `?${kept.join('&')}` : '';
};

/**
 * Public data written at Sign up so the chooser appears after verify.
 *
 * @returns {{ needsAccountRole: true }}
 */
export const pendingAccountRolePublicData = () => ({ [NEEDS_ACCOUNT_ROLE_KEY]: true });

/**
 * Public + private pending flag. Private survives if Console drops public keys.
 *
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const pendingAccountRoleData = () => ({
  publicData: pendingAccountRolePublicData(),
  privateData: pendingAccountRolePublicData(),
});

/**
 * Public data written when they pick a role.
 *
 * @param {'talk'|'listen'|'both'} role
 * @param {string|null} userType
 * @returns {Object}
 */
export const chosenAccountRolePublicData = (role, userType) => ({
  [ACCOUNT_ROLE_KEY]: role,
  [NEEDS_ACCOUNT_ROLE_KEY]: false,
  ...(userType ? { userType } : {}),
});

/**
 * Persist Talk / Listen / Both on public and private so login cannot lose it.
 *
 * @param {'talk'|'listen'|'both'} role
 * @param {string|null} userType
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const chosenAccountRoleData = (role, userType) => {
  const data = chosenAccountRolePublicData(role, userType);
  return { publicData: data, privateData: data };
};

/**
 * Persist Both (or Listen) and clear the started flag after setup finishes.
 *
 * @param {'listen'|'both'} role
 * @param {string|null} userType
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const completeListenerOnboardingData = (role, userType) => {
  const data = {
    ...chosenAccountRolePublicData(role, userType),
    [LISTENER_ONBOARDING_STARTED_KEY]: false,
  };
  return { publicData: data, privateData: data };
};

/**
 * Revert an abandoned Become a listener attempt to Talk-only.
 *
 * @param {string|null} userType
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const cancelListenerOnboardingData = userType => {
  const data = {
    ...chosenAccountRolePublicData(ACCOUNT_ROLE_TALK, userType),
    [LISTENER_ONBOARDING_STARTED_KEY]: false,
  };
  return { publicData: data, privateData: data };
};

/**
 * Sign up write. Listen card saves Listen immediately. Talk still owes the
 * Talk / Listen / Both chooser.
 *
 * @param {string|null} userType
 * @returns {{ publicData: Object, privateData: Object }}
 */
export const signupAccountRoleData = userType => {
  if (isListenerUserType(userType)) {
    return chosenAccountRoleData(ACCOUNT_ROLE_LISTEN, userType);
  }
  // Talker signup (Sam, Oct 10 2026): the talker page already says what they
  // want, so save Talk now instead of the pending flag that sent them to
  // /choose-role after verifying their email.
  if (userType) {
    return chosenAccountRoleData(ACCOUNT_ROLE_TALK, userType);
  }
  return pendingAccountRoleData();
};

/**
 * Starting Become a listener / /l/new no longer writes Both. Kept so
 * existing tests can describe who used to be upgraded; NewListingPage
 * writes listenerOnboardingStarted instead.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const shouldUpgradeTalkToBoth = currentUser => {
  if (!currentUser?.id) {
    return false;
  }
  const role = getAccountRole(currentUser);
  return role !== ACCOUNT_ROLE_LISTEN && role !== ACCOUNT_ROLE_BOTH;
};

/**
 * Listen-only (or no Talk / Both role) hitting Become a talker / ?edit=talking
 * must become Both so Talk intent cannot stay Listen-only.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const shouldUpgradeListenToBoth = currentUser => {
  if (!currentUser?.id) {
    return false;
  }
  const role = getAccountRole(currentUser);
  return role !== ACCOUNT_ROLE_TALK && role !== ACCOUNT_ROLE_BOTH;
};

/**
 * Listen inferred from userType but never written to public/private.
 * Persist on Dashboard so login cannot lose it if Console later drops userType.
 *
 * @param {Object} [currentUser]
 * @returns {'listen'|null}
 */
export const unsavedInferredListenRole = currentUser => {
  if (!currentUser?.id) {
    return null;
  }
  if (
    roleFrom(publicDataOf(currentUser)) ||
    roleFrom(privateDataOf(currentUser)) ||
    roleFrom(protectedDataOf(currentUser))
  ) {
    return null;
  }
  return inferredAccountRoleFromUserType(currentUser);
};

/**
 * True when this account already listens: a Listening profile, or Listen / Both.
 *
 * @param {Object} [params]
 * @param {Object} [params.currentUser]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {boolean}
 */
export const isExistingListener = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = getAccountRole(currentUser);
  if (role === ACCOUNT_ROLE_LISTEN || role === ACCOUNT_ROLE_BOTH) {
    return true;
  }
  if (role === ACCOUNT_ROLE_TALK) {
    return false;
  }
  // No saved role: a leftover listing is not enough. Only an explicit
  // Listen userType (not generic `provider`) counts as already listening.
  return inferredAccountRoleFromUserType(currentUser) === ACCOUNT_ROLE_LISTEN;
};

/**
 * Accounts already on the marketplace before the hard email gate:
 * a Listening profile, a saved Talk/Listen/Both role, or anyone not
 * in the brand-new `needsAccountRole` funnel.
 *
 * @param {Object} [currentUser]
 * @param {Object} [params]
 * @param {boolean} [params.currentUserHasListings]
 * @returns {boolean}
 */
export const isEstablishedAccount = (currentUser, { currentUserHasListings = false } = {}) => {
  if (!currentUser?.id) {
    return false;
  }
  if (currentUserHasListings) {
    return true;
  }
  if (getAccountRole(currentUser)) {
    return true;
  }
  if (isExistingListener({ currentUser, currentUserHasListings })) {
    return true;
  }
  return !hasPendingAccountRoleFlag(currentUser);
};

/**
 * Hard email gate for EVERY signed-in account (Sam, Oct 7 2026): nobody can
 * do anything (Guidelines, ID check, payouts, profile, Available, booking,
 * Start now, messages) until their own email address is verified. Existing
 * progress is kept; they just see "Check your inbox" until they verify.
 * Strict `=== false` so a still-loading user is never bounced.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
// Email verification is the last onboarding step now (Sam, Oct 11 2026), handled
// by the onboarding router + /onboarding, so pages no longer bounce to Check your inbox.
// eslint-disable-next-line no-unused-vars
export const needsEmailVerification = currentUser => false;

/**
 * Become a listener CTAs. Guests and Talk-only keep the link.
 * Listen / Both, or anyone who already has a Listening profile, hide it —
 * same role gate as Dashboard / Your profiles (mirror of listen-only
 * hiding Find a Listener).
 *
 * @param {Object} [params]
 * @param {Object} [params.currentUser]
 * @param {boolean} [params.currentUserHasListings]
 * @param {boolean} [params.isAuthenticated]
 * @returns {boolean}
 */
export const showBecomeAListenerCta = ({
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
  isAuthenticated,
  currentUser,
} = {}) => {
  const signedIn = isAuthenticated === true || (isAuthenticated !== false && !!currentUser?.id);
  if (!signedIn) {
    return true;
  }
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserHasListingsFetched,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  return !isExistingListener({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
};

/**
 * Talker / Listener / Both for Account settings. Same chrome source as
 * Dashboard tabs and Listen-only Find a Listener.
 *
 * @param {Object} [params]
 * @returns {'talker'|'listener'|'both'}
 */
export const accountSetupKind = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return ACCOUNT_SETUP_TALKER;
  }
  const role = getAccountRole(currentUser);
  if (role === ACCOUNT_ROLE_TALK) {
    return ACCOUNT_SETUP_TALKER;
  }
  if (role === ACCOUNT_ROLE_LISTEN) {
    return ACCOUNT_SETUP_LISTENER;
  }
  if (role === ACCOUNT_ROLE_BOTH) {
    return ACCOUNT_SETUP_BOTH;
  }
  const { showListening, showTalking } = dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  if (showListening && showTalking) {
    return ACCOUNT_SETUP_BOTH;
  }
  if (showListening) {
    return ACCOUNT_SETUP_LISTENER;
  }
  return ACCOUNT_SETUP_TALKER;
};

/**
 * Translation id for the Account settings setup value.
 *
 * @param {Object} [params]
 * @returns {string}
 */
export const accountSetupLabelId = (params = {}) => {
  const kind = accountSetupKind(params);
  if (kind === ACCOUNT_SETUP_LISTENER) {
    return 'AccountSettings.setupListener';
  }
  if (kind === ACCOUNT_SETUP_BOTH) {
    return 'AccountSettings.setupBoth';
  }
  return 'AccountSettings.setupTalker';
};

/**
 * Listen / Both with a Listening profile: show sticky Available in Settings
 * and on the Listening dashboard. Talk-only never sees it.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const showAvailableToListenControl = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const { showListening } = dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  if (!showListening) {
    return false;
  }
  return hasPublishedListeningProfile({
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
};

/**
 * Talk-only and Both can browse Search. Listen-only chrome (listen role or a
 * Listening profile with no talker role) hides Find a Listener.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const showFindAListenerCta = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  isAuthenticated,
} = {}) => {
  const signedIn = isAuthenticated === true || (isAuthenticated !== false && !!currentUser?.id);
  if (!signedIn) {
    return true;
  }
  return dashboardLayoutForUser({
    currentUser,
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  }).showTalking;
};

/**
 * True when this role can listen (Listener or Both).
 *
 * @param {'talk'|'listen'|'both'|null} [role]
 * @returns {boolean}
 */
export const accountRoleIncludesListening = role =>
  role === ACCOUNT_ROLE_LISTEN || role === ACCOUNT_ROLE_BOTH;

/**
 * True when this role can talk (Talker or Both).
 *
 * @param {'talk'|'listen'|'both'|null} [role]
 * @returns {boolean}
 */
export const accountRoleIncludesTalking = role =>
  role === ACCOUNT_ROLE_TALK || role === ACCOUNT_ROLE_BOTH;

/**
 * Talking profile is set up when they have talker-specific copy, not just a signup name.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const hasTalkingProfileSetup = currentUser => {
  const profile = currentUser?.attributes?.profile || {};
  if (typeof profile.bio === 'string' && profile.bio.trim()) {
    return true;
  }
  return hasTalkingNavSignals(currentUser);
};

/**
 * Talker-side profile fields. Do not use display bio — listeners often have one.
 *
 * @param {Object} [currentUser]
 * @returns {boolean}
 */
export const hasTalkingNavSignals = currentUser => {
  const publicData = currentUser?.attributes?.profile?.publicData || {};
  const formats = Array.isArray(publicData.preferredFormats)
    ? publicData.preferredFormats.filter(Boolean)
    : [];
  if (formats.length > 0) {
    return true;
  }
  // Topics are the main Talking profile field; they were not counted, so a
  // talker who picked topics still saw "Finish your Talking profile".
  const topics = Array.isArray(publicData.interestedTopics)
    ? publicData.interestedTopics.filter(Boolean)
    : [];
  if (topics.length > 0) {
    return true;
  }
  if (
    typeof publicData.interestedTopicsOther === 'string' &&
    !!publicData.interestedTopicsOther.trim()
  ) {
    return true;
  }
  // Saved the Talking profile form (every field there is optional).
  if (Number(publicData.talkingProfileSavedAt) > 0) {
    return true;
  }
  return typeof publicData.talkingNote === 'string' && !!publicData.talkingNote.trim();
};

/**
 * Dual-use for the hamburger Switch control.
 *
 * Saved Both always switches. A published Listening profile plus Talk
 * (saved Talk, or preferred formats / talking note) also switches. A
 * leftover draft listing does not. Dashboard chrome stays on the saved
 * role until Become a listener writes Both.
 *
 * @param {Object} [params]
 * @returns {boolean}
 */
export const isDualRoleAccount = ({
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
  currentUserHasListingsFetched,
} = {}) => {
  if (
    isPendingBecomeListener({
      currentUser,
      currentUserHasListings,
      currentUserHasListingsFetched,
      currentUserOwnListing,
      ownListings,
    })
  ) {
    return false;
  }
  const role = getAccountRole(currentUser);
  if (role === ACCOUNT_ROLE_BOTH) {
    return true;
  }
  // Saved Talk-only, Listen-only, or no role: never show the
  // Listener/Talker toggle. A leftover listing or inferred userType
  // must not default a talker-only account to Listener.
  return false;
};

/**
 * Create Profile wizard, or the existing Listening editor if a listing already exists.
 *
 * @param {Object} [params]
 * @returns {{ name: string, params?: Object }}
 */
export const listeningProfileSetupRoute = ({
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  const existing =
    editListeningProfileRoute(currentUserOwnListing) ||
    editListeningProfileRoute(pickOwnListeningProfile(ownListings));
  return existing || { name: 'NewListingPage' };
};

/**
 * Existing Talking editor on the Profile hub.
 *
 * @returns {{ name: string, to: { search: string } }}
 */
export const talkingProfileSetupRoute = () => ({
  name: 'YourProfilePage',
  to: { search: '?edit=talking' },
});

/** First-time setup (signup / dashboard checklist): shows the Optional / Skip box. */
export const talkingProfileFirstSetupRoute = () => ({
  name: 'YourProfilePage',
  to: { search: '?edit=talking&setup=1' },
});

/**
 * After Account settings changes Talker / Listener / Both, which profiles
 * they just gained and still need to finish. Already-complete sides stay silent.
 *
 * @param {Object} [params]
 * @returns {{ needListening: boolean, needTalking: boolean }}
 */
export const profileSetupNeededAfterRoleChange = ({
  previousRole = null,
  nextRole = null,
  currentUser,
  currentUserHasListings = false,
  currentUserOwnListing = null,
  ownListings = [],
} = {}) => {
  const gainedListening =
    accountRoleIncludesListening(nextRole) && !accountRoleIncludesListening(previousRole);
  const gainedTalking =
    accountRoleIncludesTalking(nextRole) && !accountRoleIncludesTalking(previousRole);
  const listeningReady = hasPublishedListeningProfile({
    currentUserHasListings,
    currentUserOwnListing,
    ownListings,
  });
  return {
    needListening: gainedListening && !listeningReady,
    needTalking: gainedTalking && !hasTalkingProfileSetup(currentUser),
  };
};
